import React, { useEffect, useRef, useState } from "react";
import { axiosInstance } from "../../../../config/http";
import { get } from "lodash";
import { DeploymentCard, DeploymentList } from "./styled";
import { useNavigate } from "react-router-dom";

export default function AllDeployments() {
  // data + ui
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // filters
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");            // upcoming | active | ended | undated | ''
  const [instanceName, setInstanceName] = useState("");

  const navigate = useNavigate();

  // debounce + cancel controls
  const debounceTimer = useRef(null);
  const controllerRef = useRef(null);                  // AbortController for axios
  const latestReqId = useRef(0);                       // ignore stale responses

  const buildParams = () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (status) params.status = status;
    if (instanceName.trim()) params.instance_name = instanceName.trim();
    return params;
  };

  const fetchDeployments = (params, reqId) => {
    // cancel in-flight request (if any)
    if (controllerRef.current) controllerRef.current.abort();
    controllerRef.current = new AbortController();

    setLoading(true);
    setErr(""); // don't clear items; prevents flicker

    axiosInstance
      .get("/deployment", { params, signal: controllerRef.current.signal })
      .then((response) => {
        if (reqId !== latestReqId.current) return; // stale response

        const data = get(response, "data", []);
        const items = Array.isArray(data) ? data : (data.items || []);
        setDeployments(items);
        setErr("");
      })
      .catch((error) => {
        // ignore abort/cancel errors or stale responses
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") return;
        if (reqId !== latestReqId.current) return;

        console.error("GET /deployment failed:", error);
        // Only show error if no cached data to display
        if (!deployments.length) setErr("Failed to load deployments.");
      })
      .finally(() => {
        if (reqId === latestReqId.current) setLoading(false);
      });
  };

  // initial load
  useEffect(() => {
    latestReqId.current += 1;
    fetchDeployments(buildParams(), latestReqId.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // re-fetch (debounced) when filters change
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      latestReqId.current += 1;
      fetchDeployments(buildParams(), latestReqId.current);
    }, 300);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status, instanceName]);

  const gotoEditPage = (name) => navigate("/edit-survey/" + name);

  return (
    <div style={{ padding: "20px" }}>
      {/* FILTER BAR (top) */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <input
          placeholder="Search name or start keyword"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{
            flex: 2,
            minWidth: 180,
            padding: "8px",
            borderRadius: 6,
            border: "1px solid #ccc",
          }}
        />
        <input
          placeholder="Instance name"
          value={instanceName}
          onChange={(e) => setInstanceName(e.target.value)}
          style={{
            flex: 1,
            minWidth: 140,
            padding: "8px",
            borderRadius: 6,
            border: "1px solid #ccc",
          }}
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          style={{
            flex: 1,
            minWidth: 140,
            padding: "8px",
            borderRadius: 6,
            border: "1px solid #ccc",
            background: "#fff",
          }}
        >
          <option value="">Any status</option>
          <option value="upcoming">Upcoming</option>
          <option value="active">Active</option>
          <option value="ended">Ended</option>
          <option value="undated">Undated</option>
        </select>

        {/* subtle loading badge */}
        {loading && (
          <span style={{ fontSize: 12, color: "#777" }}>loading…</span>
        )}
      </div>

      {err && <div style={{ color: "red", paddingBottom: 8 }}>{err}</div>}

      <DeploymentList>
        {deployments.length > 0 ? (
          deployments.map((d) => (
            <DeploymentCard
              key={d.deployment_name}
              onClick={() => gotoEditPage(d.deployment_name)}
            >
              <div>
                <span>survey name:</span>
                <br />
                <span>{d.deployment_name}</span>
              </div>
              <div>
                <span>survey instance:</span>
                <br />
                <span>{d.instance_name ?? "—"}</span>
              </div>
              <div>
                <span>user start message:</span>
                <br />
                <span>{d.user_start_message ?? "—"}</span>
              </div>
              <div>
                <span>comments:</span>
                <br />
                <span>{d.comments ?? "—"}</span>
              </div>
            </DeploymentCard>
          ))
        ) : (
          !loading && <div style={{ padding: "8px" }}>No surveys found.</div>
        )}
      </DeploymentList>
    </div>
  );
}
